import React, { useState, useEffect } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import axios from 'axios';

// Use window.location.origin to get the current domain
const BACKEND_URL = window.location.origin;

function ManagementPage() {
  const { token } = useParams();
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isAvailableFilter, setIsAvailableFilter] = useState(null);
  const [newProduct, setNewProduct] = useState({
    name: '',
    description: '',
    price: '',
    images: []
  });
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [error, setError] = useState('');
  const [isValidToken, setIsValidToken] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [editingProduct, setEditingProduct] = useState(null);
  const [removedImages, setRemovedImages] = useState([]);
  const [isErudaLoading, setIsErudaLoading] = useState(false);
  const [isErudaReady, setIsErudaReady] = useState(Boolean(window.eruda));
  const [isProcessingImages, setIsProcessingImages] = useState(false);
  const [imageProcessingMessage, setImageProcessingMessage] = useState('');
  const [lastImageProcessingError, setLastImageProcessingError] = useState('');

  const MAX_IMAGE_SIZE = 800; // Maximum width/height in pixels
  const QUALITY = 0.8; // Image quality (0.0 to 1.0)
  const TARGET_UPLOAD_FILE_SIZE = 350 * 1024;
  const MIN_IMAGE_QUALITY = 0.45;
  const IMAGE_QUALITY_STEP = 0.1;
  const UPLOADABLE_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
  const HEIC_CONVERSION_TIMEOUT_MS = 30000;

  const getFileDiagnostics = (file) => ({
    name: file?.name,
    type: file?.type,
    size: file?.size,
    lastModified: file?.lastModified,
  });

  const getErrorDiagnostics = (error) => {
    if (!error) {
      return {
        message: 'Unknown error',
        name: 'UnknownError',
        stringValue: 'Unknown error',
        keys: [],
      };
    }

    if (error instanceof Error) {
      return {
        message: error.message || 'Unknown error',
        name: error.name || 'Error',
        stack: error.stack,
        stringValue: String(error),
        keys: Object.keys(error),
      };
    }

    if (typeof error === 'string') {
      return {
        message: error,
        name: 'StringError',
        stringValue: error,
        keys: [],
      };
    }

    const plainObject = typeof error === 'object' ? error : { value: error };

    return {
      message: plainObject?.message || plainObject?.reason || plainObject?.details || 'Unknown error',
      name: plainObject?.name || 'NonErrorObject',
      stringValue: (() => {
        try {
          return JSON.stringify(plainObject);
        } catch (jsonError) {
          return String(error);
        }
      })(),
      keys: Object.keys(plainObject || {}),
      details: plainObject,
    };
  };

  const getReadableErrorMessage = (error, fallbackMessage) => {
    const diagnostics = getErrorDiagnostics(error);

    if (diagnostics.message && diagnostics.message !== 'Unknown error') {
      return diagnostics.message;
    }

    if (diagnostics.stringValue && diagnostics.stringValue !== '[object Object]') {
      return diagnostics.stringValue;
    }

    return fallbackMessage;
  };

  const stringifyDiagnostics = (value) => {
    try {
      return JSON.stringify(value, (key, currentValue) => {
        if (currentValue instanceof Error) {
          return {
            name: currentValue.name,
            message: currentValue.message,
            stack: currentValue.stack,
          };
        }

        return currentValue;
      });
    } catch (error) {
      return String(value);
    }
  };

  const isLikelyImageFile = (file) => {
    if (!file) {
      return false;
    }

    if (typeof file.type === 'string' && file.type.startsWith('image/')) {
      return true;
    }

    return /\.(jpg|jpeg|png|gif|webp|bmp|heic|heif|avif)$/i.test(file.name || '');
  };

  const isUnsupportedUploadFormat = (file) => {
    const fileType = file?.type || '';
    const fileName = file?.name || '';
    return /image\/(heic|heif|avif)/i.test(fileType) || /\.(heic|heif|avif)$/i.test(fileName);
  };

  const isHeicLikeFormat = (file) => {
    const fileType = file?.type || '';
    const fileName = file?.name || '';
    return /image\/(heic|heif)/i.test(fileType) || /\.(heic|heif)$/i.test(fileName);
  };

  const isAvifFormat = (file) => {
    const fileType = file?.type || '';
    const fileName = file?.name || '';
    return /image\/avif/i.test(fileType) || /\.avif$/i.test(fileName);
  };

  const getUploadFileName = (fileName) => {
    const normalizedName = fileName || 'image';
    const fileNameWithoutExtension = normalizedName.replace(/\.[^.]+$/, '');
    return `${fileNameWithoutExtension}.jpg`;
  };

  const logUploadDiagnostics = (stage, payload = {}) => {
    const diagnosticsPayload = {
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      editingProductId: editingProduct?.id || null,
      selectedFilesCount: selectedFiles.length,
      removedImagesCount: removedImages.length,
      ...payload,
    };

    console.log(`[AdminImageUpload] ${stage}`, diagnosticsPayload);
    console.log(`[AdminImageUploadText] ${stage} ${stringifyDiagnostics(diagnosticsPayload)}`);
  };

  const canvasToBlob = (canvas, quality) => new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Не удалось создать файл изображения после обработки.'));
          return;
        }

        resolve(blob);
      },
      'image/jpeg',
      quality
    );
  });

  const createPreviewUrl = (file) => new Promise((resolve, reject) => {
    try {
      const reader = new FileReader();

      reader.onload = () => {
        if (typeof reader.result === 'string' && reader.result.length > 0) {
          resolve(reader.result);
          return;
        }

        reject(new Error('Не удалось создать preview data URL.'));
      };

      reader.onerror = () => {
        reject(reader.error || new Error('Не удалось прочитать изображение для preview.'));
      };

      reader.readAsDataURL(file);
    } catch (error) {
      reject(error);
    }
  });

  const loadHtmlImage = ({ src, sourceKind, revokeOnCleanup = false }) => new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      resolve({
        width: img.width,
        height: img.height,
        draw: (ctx, width, height) => ctx.drawImage(img, 0, 0, width, height),
        cleanup: () => {
          if (revokeOnCleanup) {
            URL.revokeObjectURL(src);
          }
          img.src = '';
        },
        sourceKind,
      });
    };

    img.onerror = (event) => {
      if (revokeOnCleanup) {
        URL.revokeObjectURL(src);
      }

      reject({
        error: new Error('Не удалось декодировать изображение в браузере.'),
        eventType: event?.type,
        sourceKind,
      });
    };

    img.src = src;
  });

  const withTimeout = (promise, timeoutMs, timeoutMessage) => new Promise((resolve, reject) => {
    let isSettled = false;

    const timeoutId = window.setTimeout(() => {
      if (isSettled) {
        return;
      }

      isSettled = true;
      reject(new Error(timeoutMessage));
    }, timeoutMs);

    promise
      .then((result) => {
        if (isSettled) {
          return;
        }

        isSettled = true;
        window.clearTimeout(timeoutId);
        resolve(result);
      })
      .catch((error) => {
        if (isSettled) {
          return;
        }

        isSettled = true;
        window.clearTimeout(timeoutId);
        reject(error);
      });
  });

  const appendProcessedFiles = async (filesToAppend) => {
    if (!filesToAppend || filesToAppend.length === 0) {
      return;
    }

    logUploadDiagnostics('preview:create-start', {
      filesCount: filesToAppend.length,
      files: filesToAppend.map(getFileDiagnostics),
    });

    const previewResults = await Promise.allSettled(
      filesToAppend.map(async (file) => {
        const previewUrl = await createPreviewUrl(file);
        return {
          file,
          previewUrl,
        };
      })
    );

    const successfulResults = previewResults.filter(result => result.status === 'fulfilled');
    const failedResults = previewResults.filter(result => result.status === 'rejected').map(result => result.reason);

    if (successfulResults.length > 0) {
      const successfulFiles = successfulResults.map(result => result.value.file);
      const successfulPreviewUrls = successfulResults.map(result => result.value.previewUrl);

      setSelectedFiles(prevFiles => [...prevFiles, ...successfulFiles]);
      setPreviewUrls(prevUrls => [...prevUrls, ...successfulPreviewUrls]);

      logUploadDiagnostics('preview:create-success', {
        filesCount: successfulFiles.length,
        files: successfulFiles.map(getFileDiagnostics),
        previewCount: successfulPreviewUrls.length,
      });
    }

    if (failedResults.length > 0) {
      logUploadDiagnostics('preview:create-error', {
        failedCount: failedResults.length,
        failedMessages: failedResults.map(item => item?.message || String(item)),
      });

      if (successfulResults.length === 0) {
        throw new Error(failedResults[0]?.message || 'Не удалось создать preview изображений.');
      }

      alert(`Некоторые изображения были обработаны, но не удалось показать preview.\n${failedResults[0]?.message || 'Не удалось создать preview.'}`);
    }
  };

  const loadHeic2Any = () => {
    if (window.heic2any) {
      return Promise.resolve(window.heic2any);
    }

    if (window.__heic2anyLoadingPromise) {
      return window.__heic2anyLoadingPromise;
    }

    const existingScript = document.getElementById('heic2any-script');
    if (existingScript) {
      if (window.heic2any || existingScript.dataset.loaded === 'true') {
        if (window.heic2any) {
          return Promise.resolve(window.heic2any);
        }

        return Promise.reject(new Error('Библиотека конвертации HEIC отмечена как загруженная, но недоступна в окне.'));
      }

      window.__heic2anyLoadingPromise = new Promise((resolve, reject) => {
        let timeoutId;

        const cleanup = () => {
          existingScript.removeEventListener('load', handleLoad);
          existingScript.removeEventListener('error', handleError);
          if (timeoutId) {
            window.clearTimeout(timeoutId);
          }
        };

        const handleLoad = () => {
          cleanup();

          if (window.heic2any) {
            existingScript.dataset.loaded = 'true';
            window.__heic2anyLoadingPromise = null;
            resolve(window.heic2any);
            return;
          }

          window.__heic2anyLoadingPromise = null;
          reject(new Error('Библиотека конвертации HEIC загрузилась некорректно.'));
        };

        const handleError = () => {
          cleanup();
          window.__heic2anyLoadingPromise = null;
          reject(new Error('Не удалось загрузить библиотеку конвертации HEIC.'));
        };

        existingScript.addEventListener('load', handleLoad, { once: true });
        existingScript.addEventListener('error', handleError, { once: true });

        timeoutId = window.setTimeout(() => {
          cleanup();
          window.__heic2anyLoadingPromise = null;

          if (window.heic2any) {
            existingScript.dataset.loaded = 'true';
            resolve(window.heic2any);
            return;
          }

          reject(new Error('Загрузка библиотеки конвертации HEIC заняла слишком много времени.'));
        }, 10000);
      });

      return window.__heic2anyLoadingPromise;
    }

    window.__heic2anyLoadingPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.id = 'heic2any-script';
      script.src = 'https://cdn.jsdelivr.net/npm/heic2any/dist/heic2any.min.js';
      script.async = true;

      script.onload = () => {
        if (window.heic2any) {
          script.dataset.loaded = 'true';
          window.__heic2anyLoadingPromise = null;
          resolve(window.heic2any);
          return;
        }

        window.__heic2anyLoadingPromise = null;
        reject(new Error('Библиотека конвертации HEIC загрузилась некорректно.'));
      };

      script.onerror = () => {
        window.__heic2anyLoadingPromise = null;
        reject(new Error('Не удалось загрузить библиотеку конвертации HEIC.'));
      };

      document.body.appendChild(script);
    });

    return window.__heic2anyLoadingPromise;
  };

  const convertHeicFile = async (file) => {
    logUploadDiagnostics('heic:conversion-start', {
      file: getFileDiagnostics(file),
    });
    setImageProcessingMessage('Конвертируем HEIC/HEIF в JPG. Это может занять до 30 секунд.');

    logUploadDiagnostics('heic:library-load-start', {
      file: getFileDiagnostics(file),
    });

    const heic2any = await loadHeic2Any();

    logUploadDiagnostics('heic:library-load-success', {
      file: getFileDiagnostics(file),
      converterType: typeof heic2any,
    });

    logUploadDiagnostics('heic:conversion-run-start', {
      file: getFileDiagnostics(file),
    });

    let conversionResult;

    try {
      conversionResult = await withTimeout(
        heic2any({
          blob: file,
          toType: 'image/jpeg',
          quality: QUALITY,
        }),
        HEIC_CONVERSION_TIMEOUT_MS,
        'Конвертация HEIC заняла слишком много времени на этом устройстве.'
      );
    } catch (conversionRuntimeError) {
      const conversionRuntimeDiagnostics = getErrorDiagnostics(conversionRuntimeError);

      logUploadDiagnostics('heic:conversion-run-error', {
        file: getFileDiagnostics(file),
        errorMessage: conversionRuntimeDiagnostics.message,
        errorName: conversionRuntimeDiagnostics.name,
        errorKeys: conversionRuntimeDiagnostics.keys,
        errorStringValue: conversionRuntimeDiagnostics.stringValue,
        errorDetails: conversionRuntimeDiagnostics.details,
      });

      throw new Error(getReadableErrorMessage(
        conversionRuntimeError,
        'Этот HEIC-файл не удалось конвертировать на устройстве.'
      ));
    }

    const convertedBlob = Array.isArray(conversionResult)
      ? conversionResult[0]
      : conversionResult;

    if (!convertedBlob) {
      throw new Error('Конвертация HEIC не вернула изображение.');
    }

    const convertedFile = new File([convertedBlob], getUploadFileName(file.name), {
      type: 'image/jpeg',
      lastModified: Date.now(),
    });

    logUploadDiagnostics('heic:conversion-success', {
      originalFile: getFileDiagnostics(file),
      convertedFile: getFileDiagnostics(convertedFile),
    });

    return convertedFile;
  };

  const loadImageSource = async (file) => {
    if (typeof window.createImageBitmap === 'function') {
      try {
        const bitmap = await window.createImageBitmap(file);
        logUploadDiagnostics('resize:create-image-bitmap-success', {
          file: getFileDiagnostics(file),
          width: bitmap.width,
          height: bitmap.height,
        });
        return {
          width: bitmap.width,
          height: bitmap.height,
          draw: (ctx, width, height) => ctx.drawImage(bitmap, 0, 0, width, height),
          cleanup: () => bitmap.close?.(),
          sourceKind: 'createImageBitmap',
        };
      } catch (bitmapError) {
        logUploadDiagnostics('resize:create-image-bitmap-error', {
          file: getFileDiagnostics(file),
          errorMessage: bitmapError?.message,
          errorName: bitmapError?.name,
        });
      }
    }

    const objectUrl = URL.createObjectURL(file);

    try {
      const imageElementSource = await loadHtmlImage({
        src: objectUrl,
        sourceKind: 'image-element-object-url',
        revokeOnCleanup: true,
      });

      logUploadDiagnostics('resize:image-element-object-url-success', {
        file: getFileDiagnostics(file),
        width: imageElementSource.width,
        height: imageElementSource.height,
      });

      return imageElementSource;
    } catch (objectUrlError) {
      const objectUrlDiagnostics = getErrorDiagnostics(objectUrlError?.error || objectUrlError);
      logUploadDiagnostics('resize:image-element-object-url-error', {
        file: getFileDiagnostics(file),
        eventType: objectUrlError?.eventType,
        errorMessage: objectUrlDiagnostics.message,
        errorName: objectUrlDiagnostics.name,
        errorStringValue: objectUrlDiagnostics.stringValue,
        fileSizeMb: Number(((file?.size || 0) / (1024 * 1024)).toFixed(2)),
      });
    }

    try {
      const dataUrl = await createPreviewUrl(file);
      const imageElementDataUrlSource = await loadHtmlImage({
        src: dataUrl,
        sourceKind: 'image-element-data-url',
      });

      logUploadDiagnostics('resize:image-element-data-url-success', {
        file: getFileDiagnostics(file),
        width: imageElementDataUrlSource.width,
        height: imageElementDataUrlSource.height,
      });

      return imageElementDataUrlSource;
    } catch (dataUrlError) {
      const dataUrlDiagnostics = getErrorDiagnostics(dataUrlError?.error || dataUrlError);
      logUploadDiagnostics('resize:image-element-data-url-error', {
        file: getFileDiagnostics(file),
        eventType: dataUrlError?.eventType,
        errorMessage: dataUrlDiagnostics.message,
        errorName: dataUrlDiagnostics.name,
        errorStringValue: dataUrlDiagnostics.stringValue,
        fileSizeMb: Number(((file?.size || 0) / (1024 * 1024)).toFixed(2)),
      });
      throw {
        error: new Error('Не удалось загрузить изображение. Файл может быть слишком большим для этого устройства или использовать неподдерживаемый вариант JPEG/изображения.'),
        eventType: dataUrlError?.eventType,
        sourceKind: dataUrlError?.sourceKind || 'image-element-data-url',
      };
    }
  };

  const loadEruda = () => {
    if (window.eruda) {
      window.eruda.init();
      window.eruda.show();
      setIsErudaReady(true);
      logUploadDiagnostics('eruda:ready-existing');
      return Promise.resolve(window.eruda);
    }

    const existingScript = document.getElementById('eruda-script');
    if (existingScript) {
      setIsErudaLoading(true);
      return new Promise((resolve, reject) => {
        existingScript.addEventListener('load', () => {
          window.eruda?.init();
          window.eruda?.show();
          setIsErudaLoading(false);
          setIsErudaReady(Boolean(window.eruda));
          logUploadDiagnostics('eruda:loaded-from-existing-script');
          resolve(window.eruda);
        }, { once: true });
        existingScript.addEventListener('error', () => {
          setIsErudaLoading(false);
          logUploadDiagnostics('eruda:load-error-existing-script');
          reject(new Error('Не удалось загрузить Eruda.'));
        }, { once: true });
      });
    }

    setIsErudaLoading(true);
    logUploadDiagnostics('eruda:load-start');

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.id = 'eruda-script';
      script.src = 'https://cdn.jsdelivr.net/npm/eruda';
      script.async = true;

      script.onload = () => {
        window.eruda?.init();
        window.eruda?.show();
        setIsErudaLoading(false);
        setIsErudaReady(Boolean(window.eruda));
        logUploadDiagnostics('eruda:load-success');
        resolve(window.eruda);
      };

      script.onerror = () => {
        setIsErudaLoading(false);
        logUploadDiagnostics('eruda:load-error');
        reject(new Error('Не удалось загрузить Eruda.'));
      };

      document.body.appendChild(script);
    });
  };

  const handleOpenEruda = async () => {
    try {
      await loadEruda();
    } catch (erudaError) {
      console.error('Failed to initialize Eruda:', erudaError);
      alert('Не удалось открыть Eruda. Проверьте интернет-соединение и попробуйте ещё раз.');
    }
  };

  const resizeImage = async (file) => {
    logUploadDiagnostics('resize:start', {
      file: getFileDiagnostics(file),
    });

    let workingFile = file;
    let imageSource;

    if (isUnsupportedUploadFormat(file)) {
      if (isAvifFormat(file)) {
        logUploadDiagnostics('resize:unsupported-format', {
          file: getFileDiagnostics(file),
        });
        throw new Error('Формат AVIF сейчас не поддерживается. Сохраните изображение как JPG или PNG и попробуйте снова.');
      }

      if (isHeicLikeFormat(file)) {
        try {
          logUploadDiagnostics('heic:native-decode-attempt', {
            file: getFileDiagnostics(file),
          });

          imageSource = await loadImageSource(file);

          logUploadDiagnostics('heic:native-decode-success', {
            file: getFileDiagnostics(file),
            width: imageSource.width,
            height: imageSource.height,
            sourceKind: imageSource.sourceKind,
          });
        } catch (nativeDecodeError) {
          imageSource?.cleanup?.();
          imageSource = null;
          const nativeDecodeDiagnostics = getErrorDiagnostics(nativeDecodeError?.error || nativeDecodeError);

          logUploadDiagnostics('heic:native-decode-failed', {
            file: getFileDiagnostics(file),
            errorMessage: nativeDecodeDiagnostics.message,
            errorName: nativeDecodeDiagnostics.name,
            errorKeys: nativeDecodeDiagnostics.keys,
            errorStringValue: nativeDecodeDiagnostics.stringValue,
          });

          try {
            workingFile = await convertHeicFile(file);
          } catch (conversionError) {
            const conversionDiagnostics = getErrorDiagnostics(conversionError);
            logUploadDiagnostics('heic:conversion-error', {
              file: getFileDiagnostics(file),
              errorMessage: conversionDiagnostics.message,
              errorName: conversionDiagnostics.name,
              errorKeys: conversionDiagnostics.keys,
              errorStringValue: conversionDiagnostics.stringValue,
              errorDetails: conversionDiagnostics.details,
            });
            throw new Error(`Не удалось конвертировать HEIC/HEIF в JPG. ${getReadableErrorMessage(conversionError, 'Этот файл может использовать неподдерживаемый вариант HEIC. Попробуйте сохранить изображение как JPG вручную и повторить.')}`);
          }
        }
      }
    }

    try {
      if (!imageSource) {
        imageSource = await loadImageSource(workingFile);
      }

      let width = imageSource.width;
      let height = imageSource.height;

      logUploadDiagnostics('resize:image-loaded', {
        file: getFileDiagnostics(workingFile),
        originalFile: getFileDiagnostics(file),
        originalWidth: width,
        originalHeight: height,
        sourceKind: imageSource.sourceKind,
      });

      if (width > height) {
        if (width > MAX_IMAGE_SIZE) {
          height = Math.round((height * MAX_IMAGE_SIZE) / width);
          width = MAX_IMAGE_SIZE;
        }
      } else {
        if (height > MAX_IMAGE_SIZE) {
          width = Math.round((width * MAX_IMAGE_SIZE) / height);
          height = MAX_IMAGE_SIZE;
        }
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        const error = new Error('Canvas context is unavailable on this device/browser.');
        logUploadDiagnostics('resize:canvas-context-missing', {
          file: getFileDiagnostics(file),
          targetWidth: width,
          targetHeight: height,
          error: error.message,
        });
        throw error;
      }

      imageSource.draw(ctx, width, height);

      let currentQuality = QUALITY;
      let bestBlob = null;

      while (currentQuality >= MIN_IMAGE_QUALITY) {
        const blob = await canvasToBlob(canvas, currentQuality);

        logUploadDiagnostics('resize:compression-attempt', {
          file: getFileDiagnostics(workingFile),
          originalFile: getFileDiagnostics(file),
          targetWidth: width,
          targetHeight: height,
          quality: Number(currentQuality.toFixed(2)),
          blobSize: blob.size,
          targetSize: TARGET_UPLOAD_FILE_SIZE,
        });

        if (!bestBlob || blob.size < bestBlob.size) {
          bestBlob = blob;
        }

        if (blob.size <= TARGET_UPLOAD_FILE_SIZE) {
          break;
        }

        currentQuality = Number((currentQuality - IMAGE_QUALITY_STEP).toFixed(2));
      }

      if (!bestBlob) {
        logUploadDiagnostics('resize:blob-empty', {
          file: getFileDiagnostics(workingFile),
          targetWidth: width,
          targetHeight: height,
        });
        throw new Error('Не удалось обработать изображение. Попробуйте другой файл.');
      }

      const resizedFile = new File([bestBlob], getUploadFileName(file.name), {
        type: 'image/jpeg',
        lastModified: Date.now(),
      });

      logUploadDiagnostics('resize:success', {
        originalFile: getFileDiagnostics(file),
        processedFile: getFileDiagnostics(workingFile),
        resizedFile: getFileDiagnostics(resizedFile),
        targetWidth: width,
        targetHeight: height,
        sourceKind: imageSource.sourceKind,
      });

      return resizedFile;
    } catch (processingError) {
      const error = processingError?.error || processingError;
      logUploadDiagnostics('resize:image-load-error', {
        file: getFileDiagnostics(workingFile),
        originalFile: getFileDiagnostics(file),
        eventType: processingError?.eventType,
        errorMessage: error?.message,
        errorName: error?.name,
      });

      if (UPLOADABLE_IMAGE_TYPES.includes(workingFile.type) && workingFile.size <= TARGET_UPLOAD_FILE_SIZE) {
        logUploadDiagnostics('resize:fallback-original-file', {
          file: getFileDiagnostics(workingFile),
          originalFile: getFileDiagnostics(file),
        });
        return workingFile;
      }

      throw (error || new Error('Не удалось обработать изображение.'));
    } finally {
      imageSource?.cleanup?.();
    }
  };

  const handleNewImages = async (files) => {
    logUploadDiagnostics('selection:received', {
      filesCount: files.length,
      files: Array.from(files).map(getFileDiagnostics),
    });

    if (files.length === 0) {
      return;
    }

    setLastImageProcessingError('');
    setIsProcessingImages(true);
    setImageProcessingMessage(
      files.length > 1
        ? `Обрабатываем ${files.length} изображений. Пожалуйста, подождите.`
        : 'Обрабатываем изображение. Пожалуйста, подождите.'
    );

    const totalImages = editingProduct 
      ? (editingProduct.images?.length || 0) - removedImages.length + selectedFiles.length + files.length 
      : selectedFiles.length + files.length;

    if (totalImages > 5) {
      setIsProcessingImages(false);
      setImageProcessingMessage('');
      alert('Максимально 5 изображений');
      return;
    }

    try {
      const resizedFiles = [];
      const failedResults = [];

      for (const file of Array.from(files)) {
        if (!isLikelyImageFile(file)) {
          logUploadDiagnostics('selection:invalid-file-type', {
            file: getFileDiagnostics(file),
          });
          failedResults.push(new Error(`Файл ${file.name} не распознан как изображение`));
          continue;
        }

        try {
          const resizedFile = await resizeImage(file);
          setImageProcessingMessage(`Подготавливаем превью: ${resizedFiles.length + failedResults.length + 1} из ${files.length}`);
          logUploadDiagnostics('selection:file-processed', {
            originalFile: getFileDiagnostics(file),
            processedFile: getFileDiagnostics(resizedFile),
          });
          resizedFiles.push(resizedFile);
        } catch (processingError) {
          logUploadDiagnostics('selection:file-error', {
            file: getFileDiagnostics(file),
            errorMessage: processingError?.message,
            errorName: processingError?.name,
          });
          failedResults.push(processingError);
        }
      }

      logUploadDiagnostics('selection:processed', {
        filesCount: resizedFiles.length,
        files: resizedFiles.map(getFileDiagnostics),
        failedCount: failedResults.length,
        failedMessages: failedResults.map(item => item?.message || String(item)),
      });

      if (resizedFiles.length > 0) {
        await appendProcessedFiles(resizedFiles);
      }

      if (failedResults.length > 0) {
        const firstErrorMessage = failedResults[0]?.message || 'Не удалось обработать часть изображений.';
        if (resizedFiles.length === 0) {
          throw new Error(firstErrorMessage);
        }

        alert(`Некоторые изображения не удалось обработать.\n${firstErrorMessage}`);
      }
    } catch (error) {
      const errorDiagnostics = getErrorDiagnostics(error);
      const message = getReadableErrorMessage(error, 'Не удалось обработать изображение.');
      logUploadDiagnostics('selection:error', {
        errorMessage: errorDiagnostics.message,
        errorName: errorDiagnostics.name,
        errorKeys: errorDiagnostics.keys,
        errorStringValue: errorDiagnostics.stringValue,
        files: Array.from(files).map(getFileDiagnostics),
      });
      console.error('Error processing images:', error);
      setLastImageProcessingError(message);
      alert('Ошибка при обработке изображений.')
      alert(message);
    } finally {
      setIsProcessingImages(false);
      setImageProcessingMessage('');
    }
  };

  const handlePasteClick = async () => {
    try {
      logUploadDiagnostics('clipboard:read-start');

      // Check if clipboard API is available
      if (!navigator.clipboard) {
        console.error('Clipboard API not available');
        logUploadDiagnostics('clipboard:api-unavailable');
        return;
      }

      // Request permission if needed (for mobile)
      if (navigator.permissions) {
        const result = await navigator.permissions.query({ name: 'clipboard-read' });
        if (result.state === 'denied') {
          console.error('Permission to access clipboard denied');
          logUploadDiagnostics('clipboard:permission-denied');
          return;
        }
      }

      // Try to read image from clipboard
      const items = await navigator.clipboard.read();
      logUploadDiagnostics('clipboard:items-read', {
        itemCount: items.length,
        itemTypes: items.map(item => item.types),
      });
      for (const item of items) {
        if (item.types.includes('image/png') || item.types.includes('image/jpeg')) {
          const imageType = item.types.find(type => type.startsWith('image/'));
          const blob = await item.getType(imageType);
          const file = new File([blob], `pasted-image-${Date.now()}.jpg`, { type: 'image/jpeg' });
          await handleNewImages([file]);
          break;
        }
      }
    } catch (error) {
      logUploadDiagnostics('clipboard:error', {
        errorMessage: error?.message,
        errorName: error?.name,
      });
      console.error('Error accessing clipboard:', error);
      // Fallback to file input
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.multiple = true;
      input.onchange = (e) => {
        if (e.target.files.length > 0) {
          logUploadDiagnostics('clipboard:fallback-file-input', {
            filesCount: e.target.files.length,
            files: Array.from(e.target.files).map(getFileDiagnostics),
          });
          handleNewImages(Array.from(e.target.files));
        }
      };
      input.click();
    }
  };

  const handlePaste = async (e) => {
    const items = (e.clipboardData || e.originalEvent.clipboardData).items;
    let imageFile = null;

    logUploadDiagnostics('paste:event', {
      itemCount: items.length,
      itemTypes: Array.from(items).map(item => item.type),
    });

    for (const item of items) {
      if (item.type.indexOf('image') === 0) {
        imageFile = item.getAsFile();
        break;
      }
    }

    if (imageFile) {
      const totalImages = editingProduct 
        ? (editingProduct.images?.length || 0) - removedImages.length + 1
        : selectedFiles.length + 1;

      if (totalImages > 5) {
        alert('Максимально 5 изображений');
        return;
      }

      try {
        setLastImageProcessingError('');
        setIsProcessingImages(true);
        setImageProcessingMessage('Обрабатываем вставленное изображение. Пожалуйста, подождите.');
        const resizedFile = await resizeImage(imageFile);
        await appendProcessedFiles([resizedFile]);
      } catch (error) {
        const message = getReadableErrorMessage(error, 'Ошибка при обработке изображения. Пожалуйста, попробуйте другой файл.');
        logUploadDiagnostics('paste:error', {
          errorMessage: getErrorDiagnostics(error).message,
          errorName: getErrorDiagnostics(error).name,
          file: getFileDiagnostics(imageFile),
        });
        console.error('Error processing pasted image:', error);
        setLastImageProcessingError(message);
        alert(message);
      } finally {
        setIsProcessingImages(false);
        setImageProcessingMessage('');
      }
    }
  };

  useEffect(() => {
    const verifyToken = async () => {
      try {
        const response = await axios.get('/api/manage/products', {
          headers: { Authorization: token }
        });
        setIsValidToken(true);
        setProducts(response.data);
      } catch (error) {
        setIsValidToken(false);
        setError('Недействительный токен управления');
      } finally {
        setIsLoading(false);
      }
    };

    verifyToken();
  }, [token]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`/api/manage/products${searchTerm ? `?search=${encodeURIComponent(searchTerm)}` : ''}`, {
          headers: { Authorization: token }
        });
        setProducts(response.data);
      } catch (error) {
        setError('Ошибка при загрузке товаров');
        console.error('Error fetching products:', error);
      }
    };

    if (isValidToken) {
      fetchProducts();
    }
  }, [token, isValidToken, searchTerm]);

  useEffect(() => {
    let result = products;

    // Apply search filter
    if (searchTerm) {
      result = result.filter(product => 
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply availability filter
    if (isAvailableFilter !== null) {
      result = result.filter(product => 
        product.is_available === (isAvailableFilter ? 1 : 0)
      );
    }

    setFilteredProducts(result);
  }, [products, searchTerm, isAvailableFilter]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append('name', editingProduct ? editingProduct.name : newProduct.name);
    formData.append('description', editingProduct ? editingProduct.description : newProduct.description);
    formData.append('price', editingProduct ? editingProduct.price : newProduct.price);

    if (removedImages.length > 0) {
      formData.append('removedImages', JSON.stringify(removedImages));
    }

    selectedFiles.forEach(file => {
      formData.append('images', file);
    });

    logUploadDiagnostics('submit:start', {
      endpoint: editingProduct ? `/api/products/${editingProduct.id}` : '/api/products',
      method: editingProduct ? 'PUT' : 'POST',
      files: selectedFiles.map(getFileDiagnostics),
      totalUploadSize: selectedFiles.reduce((total, file) => total + (file?.size || 0), 0),
      formEntries: Array.from(formData.entries()).map(([key, value]) => ({
        key,
        value: value instanceof File ? getFileDiagnostics(value) : value,
      })),
    });

    try {
      if (editingProduct) {
        await axios.put(
          `/api/products/${editingProduct.id}`,
          formData,
          {
            headers: {
              'Authorization': token
            }
          }
        );
      } else {
        await axios.post(
          '/api/products',
          formData,
          {
            headers: {
              'Authorization': token
            }
          }
        );
      }

      logUploadDiagnostics('submit:success', {
        endpoint: editingProduct ? `/api/products/${editingProduct.id}` : '/api/products',
        method: editingProduct ? 'PUT' : 'POST',
      });

      setEditingProduct(null);
      setNewProduct({
        name: '',
        description: '',
        price: '',
        images: []
      });
      setSelectedFiles([]);
      setPreviewUrls([]);
      setRemovedImages([]);
      
      const fetchProducts = async () => {
        try {
          const response = await axios.get(`/api/manage/products${searchTerm ? `?search=${encodeURIComponent(searchTerm)}` : ''}`, {
            headers: { Authorization: token }
          });
          setProducts(response.data);
        } catch (error) {
          setError('Ошибка при загрузке товаров');
          console.error('Error fetching products:', error);
        }
      };

      fetchProducts();
    } catch (error) {
      logUploadDiagnostics('submit:error', {
        endpoint: editingProduct ? `/api/products/${editingProduct.id}` : '/api/products',
        method: editingProduct ? 'PUT' : 'POST',
        errorMessage: error?.message,
        responseStatus: error?.response?.status,
        responseData: error?.response?.data,
        requestHeaders: error?.config?.headers,
      });
      console.error('Upload request failed:', {
        message: error?.message,
        status: error?.response?.status,
        data: error?.response?.data,
      });
      setError(error.response?.data?.error || 'Произошла ошибка');
    }
  };

  const handleDelete = async (product) => {
    if (window.confirm(`Вы уверены, что хотите удалить товар "${product.name}"?`)) {
      try {
        await axios.delete(`/api/products/${product.id}`, {
          headers: { Authorization: token }
        });
        setProducts(products.filter(p => p.id !== product.id));
      } catch (error) {
        setError('Ошибка при удалении товара');
      }
    }
  };

  const handleToggleAvailability = async (product) => {
    try {
      await axios.put(
        `/api/products/${product.id}/availability`,
        { is_available: !product.is_available },
        { headers: { Authorization: token } }
      );
      setProducts(products.map(p =>
        p.id === product.id ? { ...p, is_available: !p.is_available } : p
      ));
    } catch (error) {
      setError('Ошибка при изменении доступности товара');
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setRemovedImages([]);
    setSelectedFiles([]);
    setPreviewUrls([]);
  };

  const handleCancel = () => {
    setEditingProduct(null);
    setRemovedImages([]);
    setSelectedFiles([]);
    setPreviewUrls([]);
    setNewProduct({
      name: '',
      description: '',
      price: '',
      images: []
    });
  };

  const handleRemoveImage = (imagePath) => {
    if (editingProduct) {
      setRemovedImages([...removedImages, imagePath]);
    }
  };

  const handleRemoveNewImage = (index) => {
    const newFiles = [...selectedFiles];
    newFiles.splice(index, 1);
    setSelectedFiles(newFiles);

    const newUrls = [...previewUrls];
    if (typeof newUrls[index] === 'string' && newUrls[index].startsWith('blob:')) {
      URL.revokeObjectURL(newUrls[index]);
    }
    newUrls.splice(index, 1);
    setPreviewUrls(newUrls);
  };

  useEffect(() => {
    window.addEventListener('paste', handlePaste);
    return () => {
      window.removeEventListener('paste', handlePaste);
    };
  }, [editingProduct, removedImages, selectedFiles]);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.get('eruda') === '1') {
      handleOpenEruda();
    }
  }, []);

  useEffect(() => {
    logUploadDiagnostics('state:selected-files-updated', {
      filesCount: selectedFiles.length,
      files: selectedFiles.map(getFileDiagnostics),
      previewCount: previewUrls.length,
    });
  }, [selectedFiles, previewUrls]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (editingProduct) {
      setEditingProduct(prev => ({
        ...prev,
        [name]: value
      }));
    } else {
      setNewProduct(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  if (isLoading) {
    return (
      <div className="text-center mt-8">
        <p>Загрузка...</p>
      </div>
    );
  }

  if (!isValidToken) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-bold">Управление товарами</h2>
        <div className="flex items-center gap-3">
          <Link to={`/reserved/${token}`} className="text-blue-500 hover:text-blue-700">
            Зарезервированные товары
          </Link>
        </div>
      </div>
      <form id="productForm" onSubmit={handleSubmit} className="mb-8 bg-white shadow-md rounded px-8 pt-6 pb-8">
        <h2 className="text-2xl font-bold mb-4">
          {editingProduct ? 'Редактировать товар' : 'Добавить новый товар'}
        </h2>
        
        <div className="mb-4">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="name">
            Название
          </label>
          <input
            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            id="name"
            type="text"
            name="name"
            value={editingProduct ? editingProduct.name : newProduct.name}
            onChange={handleInputChange}
            required
          />
        </div>

        <div className="mb-4">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="description">
            Описание
          </label>
          <textarea
            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            id="description"
            name="description"
            value={editingProduct ? editingProduct.description : newProduct.description}
            onChange={handleInputChange}
            rows="4"
          />
        </div>

        <div className="mb-4">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="price">
            Цена
          </label>
          <input
            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            id="price"
            type="number"
            name="price"
            min="0"
            step="0.01"
            value={editingProduct ? editingProduct.price : newProduct.price}
            onChange={handleInputChange}
            required
          />
        </div>

        <div className="mb-4">
          <label className="block text-gray-700 text-sm font-bold mb-2">
            Изображения (максимум 5)
          </label>
          <div className="flex gap-2 mb-2">
            <button
              type="button"
              onClick={handlePasteClick}
              disabled={isProcessingImages}
              className="bg-blue-500 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline text-sm"
            >
              {isProcessingImages ? 'Подождите...' : 'Вставить изображение'}
            </button>
            <input
              type="file"
              accept="image/*"
              multiple
              disabled={isProcessingImages}
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                logUploadDiagnostics('file-input:change', {
                  filesCount: files.length,
                  files: files.map(getFileDiagnostics),
                });
                handleNewImages(files);
                e.target.value = '';
              }}
              className="hidden"
              id="image-upload"
            />
            <label
              htmlFor="image-upload"
              className={`bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline text-sm ${isProcessingImages ? 'opacity-50 cursor-not-allowed pointer-events-none' : 'cursor-pointer'}`}
            >
              {isProcessingImages ? 'Идёт обработка...' : 'Выбрать файлы'}
            </label>
          </div>
          <p className="text-sm text-gray-600 mb-2">
            Вставьте изображение из буфера обмена или выберите файлы (максимум 5 изображений)
          </p>
          {isProcessingImages && (
            <div className="mb-3 rounded border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800">
              <p className="font-medium">Обработка изображения выполняется</p>
              <p>{imageProcessingMessage || 'Пожалуйста, подождите. На некоторых устройствах конвертация может занять до 30 секунд.'}</p>
            </div>
          )}
          {lastImageProcessingError && (
            <div className="mb-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
              <p className="font-medium">Последняя ошибка обработки</p>
              <p>{lastImageProcessingError}</p>
            </div>
          )}
          <div className="flex flex-wrap gap-4 mb-4">
            {editingProduct && editingProduct.images && editingProduct.images
              .filter(img => !removedImages.includes(img))
              .map((img, index) => (
                <div key={index} className="relative w-32 h-32">
                  <img
                    src={`${BACKEND_URL}/uploads/${img}`}
                    alt={`Product ${index + 1}`}
                    className="w-full h-full object-cover rounded"
                    onError={(e) => {
                      console.error('Error loading image:', e);
                      e.target.src = 'https://via.placeholder.com/150?text=No+Image';
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => handleRemoveImage(img)}
                    className="absolute top-0 right-0 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center"
                  >
                    ×
                  </button>
                </div>
              ))}
            {previewUrls.map((url, index) => (
              <div key={`preview-${index}`} className="relative w-32 h-32">
                <img
                  src={url}
                  alt={`Preview ${index + 1}`}
                  className="w-full h-full object-cover rounded"
                  onError={(e) => {
                    console.error('Error loading preview image:', e);
                    e.target.src = 'https://via.placeholder.com/150?text=Preview';
                  }}
                />
                <button
                  type="button"
                  onClick={() => handleRemoveNewImage(index)}
                  className="absolute top-0 right-0 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="flex gap-4">
          <button
            type="submit"
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
          >
            {editingProduct ? 'Сохранить изменения' : 'Добавить товар'}
          </button>
          {editingProduct && (
            <button
              type="button"
              onClick={handleCancel}
              className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
            >
              Отменить
            </button>
          )}
        </div>
      </form>

      {/* Search input */}
      <div className="mb-8">
        <input 
          type="text" 
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Поиск товаров..."
          className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-red-500"
        />
      </div>

      {/* Availability filter */}
      <div className="mb-8">
        <label className="block text-gray-700 text-sm font-bold mb-2">Фильтр по доступности:</label>
        <div>
          <input 
            type="radio" 
            name="availabilityFilter"
            checked={isAvailableFilter === null}
            onChange={() => setIsAvailableFilter(null)}
          />
          <span className="ml-2">Все</span>
          <input 
            type="radio" 
            name="availabilityFilter"
            checked={isAvailableFilter === true}
            onChange={() => setIsAvailableFilter(true)}
            className="ml-4"
          />
          <span className="ml-2">Доступные</span>
          <input 
            type="radio" 
            name="availabilityFilter"
            checked={isAvailableFilter === false}
            onChange={() => setIsAvailableFilter(false)}
            className="ml-4"
          />
          <span className="ml-2">Зарезервированные</span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredProducts.map(product => (
          <div key={product.id} className="bg-white shadow-md rounded-lg p-4 flex flex-col">
            <div className="flex flex-wrap gap-2 mb-4">
              {product.images && product.images.map((img, index) => (
                <div key={index} className="relative w-32 h-32">
                  <img
                    key={index}
                    src={`${BACKEND_URL}/uploads/${img}`}
                    alt={`${product.name} ${index + 1}`}
                    className="w-full h-full object-cover rounded"
                    onError={(e) => {
                      console.error('Error loading image:', e);
                      e.target.src = 'https://via.placeholder.com/150?text=No+Image';
                    }}
                  />
                </div>
              ))}
            </div>
            <div className="flex-grow">
              <h3 className="text-xl font-bold mb-2">{product.name}</h3>
              <p className="text-gray-600 mb-2">{product.description}</p>
              <p className="text-lg font-bold mb-4">{product.price} €</p>
              <p className="text-gray-600 mb-2">
                {product.buyer_phone && product.buyer_phone.trim() !== '' 
                  ? `Номер покупателя: ${product.buyer_phone}` 
                  : 'Нет'}
              </p>
            </div>
            <div className="flex flex-col gap-2">
              <button
                onClick={() => handleEdit(product)}
                className="w-full bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-3 rounded focus:outline-none focus:shadow-outline text-sm"
              >
                Редактировать
              </button>
              <div className="flex gap-2">
                <button
                  onClick={() => handleToggleAvailability(product)}
                  className={`flex-1 font-bold py-2 px-3 rounded focus:outline-none focus:shadow-outline text-white text-sm ${
                    product.is_available ? 'bg-yellow-500 hover:bg-yellow-600' : 'bg-green-500 hover:bg-green-600'
                  }`}
                >
                  {product.is_available ? 'Скрыть' : 'Показать'}
                </button>
                <button
                  onClick={() => handleDelete(product)}
                  className="flex-1 bg-red-500 hover:bg-red-600 text-white font-bold py-2 px-3 rounded focus:outline-none focus:shadow-outline text-sm"
                >
                  Удалить
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default ManagementPage;
